/**
 * Data Transformation Utilities for Analyst Bench
 *
 * Transforms data from AnalystBench context format to
 * the format expected by shared rating components.
 */

import type { PeriodData, InstitutionData, CAMELScore } from '../../../context/AnalystBenchContext';
import type { CAMELComponent, CAMELCode } from '../../../constants/camelConstants';

/**
 * Transform CAMELScore[] to CAMELComponent[] format for RadarChart
 * Maps the generic string code to typed CAMELCode
 */
export function transformCamelData(camelScores: CAMELScore[]): CAMELComponent[] {
  return camelScores.map((c) => ({
    code: c.code as CAMELCode,
    score: c.score,
  }));
}

/**
 * Get previous period's score for delta display in GalaxyScoreDisplay
 * Looks for the period before the current one in the history array
 */
export function getPreviousScore(history: PeriodData[], currentPeriod: string): number {
  if (!history || history.length === 0) return 0;

  // Sort history by date descending (most recent first)
  const sortedHistory = [...history].sort((a, b) => b.date.localeCompare(a.date));

  // Find the current period index
  const currentIdx = sortedHistory.findIndex((h) => h.date === currentPeriod);

  // If not found or is the last (oldest) period, return the current period's score
  if (currentIdx < 0 || currentIdx >= sortedHistory.length - 1) {
    return sortedHistory[0]?.finalScore || 0;
  }

  // Return the next (older) period's score
  return sortedHistory[currentIdx + 1]?.finalScore || 0;
}

/**
 * Get all history including current period for an institution
 * Combines current period data with history array
 */
export function getFullHistory(institutionData: InstitutionData | null): PeriodData[] {
  if (!institutionData) return [];

  // Start with current period
  const currentPeriod: PeriodData = {
    date: institutionData.date,
    rating: institutionData.rating,
    finalScore: institutionData.finalScore,
    camel: institutionData.camel,
    sankeyIndicators: institutionData.sankeyIndicators,
  };

  // Add history
  const allPeriods = [currentPeriod, ...(institutionData.history || [])];

  // Sort by date descending and deduplicate
  const seen = new Set<string>();
  return allPeriods
    .sort((a, b) => b.date.localeCompare(a.date))
    .filter((p) => {
      if (seen.has(p.date)) return false;
      seen.add(p.date);
      return true;
    });
}
